import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../authContext';
import PublicNav from '../components/PublicNav';
import { theme } from '../lib/theme';
import { BOOKING_PACKAGES, submitBooking } from '../lib/booking';

const AREAS = ['Victoria Island', 'Lekki', 'Ikoyi', 'Lagos Island', 'Ajah', 'Surulere', 'Ikeja', 'Yaba', 'Other'];

const inp = {
  width: '100%',
  boxSizing: 'border-box',
  background: theme.bg2,
  border: '1px solid rgba(255,255,255,0.08)',
  color: theme.text,
  borderRadius: 10,
  padding: '13px 16px',
  fontSize: 15,
  outline: 'none',
};

export default function BookDriver() {
  const { session, profile } = useAuth();
  const isMarketplacePassenger =
    Boolean(session?.user) && profile?.profile_scope === 'marketplace' && profile?.role === 'passenger';

  const [step, setStep] = useState(0);
  const [mode, setMode] = useState(null);
  const [duration, setDuration] = useState(null);
  const [area, setArea] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [pickup, setPickup] = useState('');
  const [notes, setNotes] = useState('');
  const [name, setName] = useState(profile?.full_name || '');
  const [phone, setPhone] = useState(profile?.phone || '');
  const [emergency, setEmergency] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    if (!name.trim() || !phone.trim()) {
      setError('Name and phone are required.');
      return;
    }
    setLoading(true);
    try {
      const { error: err } = await submitBooking({
        authenticated: isMarketplacePassenger,
        mode,
        packageKey: duration,
        startDate: date,
        startTime: time,
        pickup: pickup || area,
        area,
        notes,
        emergencyContact: emergency,
        passengerName: name.trim(),
        passengerPhone: phone.trim(),
      });
      if (err) throw err;
      setDone(true);
    } catch (e) {
      setError(e.message || 'Booking failed.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text, fontFamily: theme.font, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 440 }}>
          <div style={{ fontSize: 56, marginBottom: 20 }}>✅</div>
          <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 12 }}>Booking submitted</h2>
          <p style={{ color: theme.muted, lineHeight: 1.7, marginBottom: 28 }}>
            We will confirm your driver within 2 hours.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/" style={{ padding: '12px 24px', background: theme.accent, color: '#fff', borderRadius: 10, textDecoration: 'none', fontWeight: 600 }}>
              Home
            </Link>
            {isMarketplacePassenger && (
              <Link to="/marketplace/passenger" style={{ padding: '12px 24px', border: '1px solid rgba(255,255,255,0.1)', color: theme.text, borderRadius: 10, textDecoration: 'none' }}>
                My bookings
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text, fontFamily: theme.font }}>
      <PublicNav />
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '96px 24px 80px' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 8 }}>Book a driver</h1>
        <p style={{ color: theme.muted, marginBottom: 28, fontSize: 14 }}>Lagos · verified drivers · diaspora-friendly</p>
        {error && <p style={{ color: '#f87171', marginBottom: 16 }}>{error}</p>}

        {step === 0 && (
          <>
            <p style={{ fontWeight: 600, marginBottom: 16 }}>Choose mode</p>
            {[
              { key: 'my-car', title: 'My car', desc: 'Driver drives your vehicle' },
              { key: 'driver-car', title: 'Driver + car', desc: 'Driver brings a vehicle' },
            ].map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMode(m.key)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  marginBottom: 10,
                  padding: 16,
                  borderRadius: 12,
                  border: `2px solid ${mode === m.key ? theme.accent : 'rgba(255,255,255,0.08)'}`,
                  background: mode === m.key ? 'rgba(13,115,119,0.12)' : theme.bg1,
                  color: theme.text,
                  cursor: 'pointer',
                }}
              >
                <strong>{m.title}</strong>
                <span style={{ display: 'block', color: theme.muted, fontSize: 13, marginTop: 4 }}>{m.desc}</span>
              </button>
            ))}
            <button type="button" disabled={!mode} onClick={() => setStep(1)} style={{ marginTop: 20, width: '100%', padding: 14, background: theme.accent, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: mode ? 'pointer' : 'not-allowed', opacity: mode ? 1 : 0.5 }}>
              Continue
            </button>
          </>
        )}

        {step === 1 && (
          <>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 13, color: theme.muted }}>Duration</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
              {BOOKING_PACKAGES.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setDuration(d.key)}
                  style={{
                    padding: 14,
                    borderRadius: 10,
                    border: `2px solid ${duration === d.key ? theme.accent : 'rgba(255,255,255,0.08)'}`,
                    background: theme.bg1,
                    color: theme.text,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{d.label}</div>
                  <div style={{ fontSize: 12, color: theme.muted }}>₦{d.price.toLocaleString()}</div>
                </button>
              ))}
            </div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: theme.muted }}>Area</label>
            <select value={area} onChange={(e) => setArea(e.target.value)} style={inp}>
              <option value="">Select area</option>
              {AREAS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <label style={{ display: 'block', marginTop: 14, marginBottom: 6, fontSize: 13, color: theme.muted }}>Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inp} />
            <label style={{ display: 'block', marginTop: 14, marginBottom: 6, fontSize: 13, color: theme.muted }}>Time</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={inp} />
            <label style={{ display: 'block', marginTop: 14, marginBottom: 6, fontSize: 13, color: theme.muted }}>Pickup address</label>
            <input value={pickup} onChange={(e) => setPickup(e.target.value)} style={inp} placeholder="Where should the driver meet you?" />
            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button type="button" onClick={() => setStep(0)} style={{ flex: 1, padding: 12, background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: theme.text, borderRadius: 10, cursor: 'pointer' }}>
                Back
              </button>
              <button type="button" disabled={!duration || !area || !date || !time} onClick={() => setStep(2)} style={{ flex: 2, padding: 12, background: theme.accent, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', opacity: duration && area && date && time ? 1 : 0.5 }}>
                Continue
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: theme.muted }}>Your name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} style={inp} required />
            <label style={{ display: 'block', marginTop: 14, marginBottom: 6, fontSize: 13, color: theme.muted }}>Phone</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} style={inp} required />
            <label style={{ display: 'block', marginTop: 14, marginBottom: 6, fontSize: 13, color: theme.muted }}>Emergency contact (optional)</label>
            <input value={emergency} onChange={(e) => setEmergency(e.target.value)} style={inp} />
            <label style={{ display: 'block', marginTop: 14, marginBottom: 6, fontSize: 13, color: theme.muted }}>Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} />
            {!session?.user && (
              <p style={{ fontSize: 13, color: theme.muted, marginTop: 16 }}>
                <Link to="/marketplace/register" style={{ color: theme.accentLight }}>Create an account</Link> to manage bookings online.
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button type="button" onClick={() => setStep(1)} style={{ flex: 1, padding: 12, background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: theme.text, borderRadius: 10, cursor: 'pointer' }}>
                Back
              </button>
              <button type="button" disabled={loading || !name || !phone} onClick={submit} style={{ flex: 2, padding: 12, background: theme.accent, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>
                {loading ? 'Submitting…' : 'Confirm booking'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
